/**
 * Listen & Belege — V1a
 *
 * Zwei Tabs:
 *   1. Arbeitslisten (voll editierbar) — CRUD/Duplicate/Item-Abhaken.
 *   2. Buchungsbelege (nur ansehen/drucken/löschen/regenerieren).
 *
 * Wichtige fachliche Regeln (V1a):
 *   * Arbeitslisten verändern NIEMALS Bestand.
 *   * Buchungsbelege sind Ableitungen aus operation_id — löschen ändert
 *     NIE die Transaktionen oder den Bestand.
 *   * Print-Flow läuft ausschließlich über ``lib/print.js`` (kein
 *     Opera-Hack, kein UA-Sniffing).
 */
import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Copy, Trash2, Pencil, Printer, X, Check,
  Search, ClipboardList, ListChecks,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { openPrintReport } from "../lib/print";

const TYPE_LABEL = {
  generic: "Allgemein",
  purchase: "Einkauf",
  transfer: "Umlagerung",
  pack: "Packliste",
};
const TYPE_ORDER = ["generic", "purchase", "transfer", "pack"];
const STATUS_LABEL = { offen: "Offen", erledigt: "Erledigt" };

const ALL = "__all__";
const NONE = "__none__";

export default function ListenBelege() {
  return (
    <div data-testid="listen-belege-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-cyan-400">
            Operations
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight truncate">
            Arbeitslisten
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Freie Kommissionier- / Einkaufs- / Umlagerungs-Listen. Verändern
            keinen Bestand. Buchungsbelege siehe Buchungshistorie.
          </p>
        </div>
      </div>

      <WorkListsTab />
    </div>
  );
}

/* --------------------------------------------------------------------- */
/* ---------------------------- Work Lists ----------------------------- */
/* --------------------------------------------------------------------- */

function WorkListsTab() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create

  const params = useMemo(() => {
    const p = {};
    if (statusFilter !== ALL) p.status = statusFilter;
    if (typeFilter !== ALL) p.type = typeFilter;
    return p;
  }, [statusFilter, typeFilter]);

  const lists = useQuery({
    queryKey: ["work-lists", params],
    queryFn: async () => (await api.get("/work-lists", { params })).data,
  });
  const products = useQuery({
    queryKey: ["products", { active: true }],
    queryFn: async () => (await api.get("/products", { params: { active: true } })).data,
    staleTime: 60_000,
  });
  const locations = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations")).data,
    staleTime: 60_000,
  });

  const productMap = useMemo(() => {
    const m = {};
    (products.data || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [products.data]);

  const locationMap = useMemo(() => {
    const m = {};
    (locations.data || []).forEach((l) => { m[l.id] = l; });
    return m;
  }, [locations.data]);

  const duplicateMut = useMutation({
    mutationFn: (id) => api.post(`/work-lists/${id}/duplicate`),
    onSuccess: () => {
      toast.success("Arbeitsliste dupliziert");
      qc.invalidateQueries({ queryKey: ["work-lists"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/work-lists/${id}`),
    onSuccess: () => {
      toast.success("Arbeitsliste gelöscht");
      qc.invalidateQueries({ queryKey: ["work-lists"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const toggleItemMut = useMutation({
    mutationFn: ({ wlId, itemId, completed }) =>
      api.patch(`/work-lists/${wlId}/items/${itemId}`, { completed }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["work-lists"] }),
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const printList = (wl) => {
    const fromName = wl.from_location_id
      ? locationMap[wl.from_location_id]?.name || "—"
      : "—";
    const toName = wl.to_location_id
      ? locationMap[wl.to_location_id]?.name || "—"
      : "—";
    const filters = [
      { label: "Titel", value: wl.title },
      { label: "Typ", value: TYPE_LABEL[wl.type] || wl.type },
      { label: "Status", value: STATUS_LABEL[wl.status] || wl.status },
      { label: "Von", value: fromName },
      { label: "Nach", value: toName },
    ];
    if (wl.notes) filters.push({ label: "Bemerkung", value: wl.notes });
    const rows = (wl.items || []).map((it) => {
      const p = productMap[it.product_id];
      return [
        it.completed ? "✓" : "☐",
        p?.name || it.product_id,
        p?.ean || "",
        p?.internal_id || "",
        String(it.quantity),
        it.notes || "",
      ];
    });
    openPrintReport({
      area: `Arbeitsliste – ${wl.title}`,
      filters,
      columns: [
        { label: "Erl.", align: "center" },
        { label: "Produkt" },
        { label: "EAN" },
        { label: "SKU" },
        { label: "Menge", align: "right" },
        { label: "Bemerkung" },
      ],
      rows,
      resultCount: rows.length,
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              data-testid="wl-filter-status"
              className="h-11 bg-zinc-900 border-zinc-800 rounded-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              <SelectItem value={ALL}>Alle Status</SelectItem>
              <SelectItem value="offen">Offen</SelectItem>
              <SelectItem value="erledigt">Erledigt</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger
              data-testid="wl-filter-type"
              className="h-11 bg-zinc-900 border-zinc-800 rounded-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              <SelectItem value={ALL}>Alle Typen</SelectItem>
              {TYPE_ORDER.map((t) => (
                <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          data-testid="wl-create-btn"
          onClick={() => { setEditing(null); setEditorOpen(true); }}
          className="h-11 bg-cyan-500 hover:bg-cyan-400 text-zinc-950 uppercase tracking-widest font-bold rounded-sm shrink-0"
        >
          <Plus size={16} className="mr-2" /> Neue Liste
        </Button>
      </div>

      {lists.isLoading && (
        <div className="text-zinc-500 py-8 text-center">Lade Arbeitslisten…</div>
      )}

      {!lists.isLoading && (lists.data || []).length === 0 && (
        <div
          data-testid="wl-empty"
          className="border border-dashed border-zinc-800 rounded-sm p-10 text-center text-zinc-500"
        >
          <ClipboardList className="mx-auto mb-3 text-zinc-600" size={32} />
          Noch keine Arbeitslisten vorhanden.
          <div className="text-xs mt-1">
            Lege eine neue Liste an — für Einkauf, Umlagerung oder Kommissionierung.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {(lists.data || []).map((wl) => (
          <WorkListCard
            key={wl.id}
            wl={wl}
            productMap={productMap}
            locationMap={locationMap}
            onEdit={() => { setEditing(wl); setEditorOpen(true); }}
            onDuplicate={() => duplicateMut.mutate(wl.id)}
            onDelete={() => {
              if (window.confirm(`Arbeitsliste „${wl.title}" wirklich löschen?`)) {
                deleteMut.mutate(wl.id);
              }
            }}
            onToggleItem={(itemId, completed) =>
              toggleItemMut.mutate({ wlId: wl.id, itemId, completed })
            }
            onPrint={() => printList(wl)}
          />
        ))}
      </div>

      {editorOpen && (
        <WorkListEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          initial={editing}
          products={products.data || []}
          locations={locations.data || []}
          onSaved={() => {
            setEditorOpen(false);
            qc.invalidateQueries({ queryKey: ["work-lists"] });
          }}
        />
      )}
    </div>
  );
}

function WorkListCard({
  wl, productMap, locationMap, onEdit, onDuplicate, onDelete, onToggleItem, onPrint,
}) {
  const fromLoc = wl.from_location_id ? locationMap[wl.from_location_id] : null;
  const toLoc = wl.to_location_id ? locationMap[wl.to_location_id] : null;
  const done = wl.items.filter((i) => i.completed).length;
  const total = wl.items.length;
  return (
    <div
      data-testid={`wl-card-${wl.id}`}
      className="border border-zinc-800 bg-zinc-900 rounded-sm p-4 flex flex-col"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-semibold text-zinc-100 truncate" data-testid="wl-card-title">
            {wl.title}
          </div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mt-0.5">
            {TYPE_LABEL[wl.type] || wl.type}
          </div>
        </div>
        <span
          data-testid={`wl-status-${wl.id}`}
          className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${
            wl.status === "erledigt"
              ? "text-emerald-400 border-emerald-600 bg-emerald-950/40"
              : "text-amber-400 border-amber-600 bg-amber-950/30"
          }`}
        >
          {STATUS_LABEL[wl.status] || wl.status}
        </span>
      </div>

      {(fromLoc || toLoc) && (
        <div className="text-xs text-zinc-400 mb-2">
          {fromLoc?.name || "—"} → {toLoc?.name || "—"}
        </div>
      )}
      {wl.notes && (
        <div className="text-xs text-zinc-400 italic mb-2 line-clamp-2">
          „{wl.notes}"
        </div>
      )}

      <div className="text-[11px] text-zinc-500 mb-2">
        {done} / {total} erledigt
      </div>

      <div className="border-t border-zinc-800 pt-2 flex-1 max-h-64 overflow-y-auto space-y-1">
        {wl.items.length === 0 && (
          <div className="text-xs text-zinc-600 italic">Keine Positionen.</div>
        )}
        {wl.items.map((it) => {
          const p = productMap[it.product_id];
          return (
            <label
              key={it.id}
              data-testid={`wl-item-${it.id}`}
              className="flex items-center gap-2 text-xs text-zinc-200 cursor-pointer hover:bg-zinc-800/40 rounded-sm px-1 py-1"
            >
              <Checkbox
                data-testid={`wl-item-check-${it.id}`}
                checked={it.completed}
                onCheckedChange={(v) => onToggleItem(it.id, !!v)}
              />
              <span className={`flex-1 truncate ${it.completed ? "line-through text-zinc-500" : ""}`}>
                {p?.name || it.product_id}
                {p?.ean && (
                  <span className="ml-2 text-zinc-500 font-mono">{p.ean}</span>
                )}
              </span>
              <span className="font-mono tabular-nums text-zinc-400">×{it.quantity}</span>
            </label>
          );
        })}
      </div>

      <div className="border-t border-zinc-800 pt-2 mt-2 flex flex-wrap gap-2">
        <Button
          data-testid={`wl-edit-${wl.id}`}
          size="sm" variant="outline"
          className="border-zinc-700 rounded-sm hover:bg-zinc-800"
          onClick={onEdit}
        >
          <Pencil size={12} className="mr-1" /> Bearbeiten
        </Button>
        <Button
          data-testid={`wl-print-${wl.id}`}
          size="sm" variant="outline"
          className="border-zinc-700 rounded-sm hover:bg-zinc-800"
          onClick={onPrint}
        >
          <Printer size={12} className="mr-1" /> Drucken
        </Button>
        <Button
          data-testid={`wl-duplicate-${wl.id}`}
          size="sm" variant="outline"
          className="border-zinc-700 rounded-sm hover:bg-zinc-800"
          onClick={onDuplicate}
        >
          <Copy size={12} className="mr-1" /> Duplizieren
        </Button>
        <Button
          data-testid={`wl-delete-${wl.id}`}
          size="sm" variant="outline"
          className="border-rose-800 text-rose-400 rounded-sm hover:bg-rose-950/60 hover:text-rose-300 ml-auto"
          onClick={onDelete}
        >
          <Trash2 size={12} />
        </Button>
      </div>
    </div>
  );
}

/* ------------------------ Work List Editor --------------------------- */

function WorkListEditor({ open, onOpenChange, initial, products, locations, onSaved }) {
  const isEdit = !!initial;
  const [title, setTitle] = useState(initial?.title || "");
  const [type, setType] = useState(initial?.type || "generic");
  const [status, setStatus] = useState(initial?.status || "offen");
  const [fromLoc, setFromLoc] = useState(initial?.from_location_id || NONE);
  const [toLoc, setToLoc] = useState(initial?.to_location_id || NONE);
  const [notes, setNotes] = useState(initial?.notes || "");
  const [items, setItems] = useState(
    (initial?.items || []).map((it) => ({
      _key: it.id,
      product_id: it.product_id,
      quantity: it.quantity,
      completed: it.completed,
      notes: it.notes || "",
    }))
  );
  const [productSearch, setProductSearch] = useState("");
  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return (products || []).slice(0, 25);
    return (products || [])
      .filter((p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.ean || "").toLowerCase().includes(q) ||
        (p.internal_id || "").toLowerCase().includes(q) ||
        (p.brand || "").toLowerCase().includes(q)
      )
      .slice(0, 25);
  }, [productSearch, products]);

  const productMap = useMemo(() => {
    const m = {};
    (products || []).forEach((p) => { m[p.id] = p; });
    return m;
  }, [products]);

  const addItem = (p) => {
    setItems((cur) => {
      const existing = cur.findIndex((it) => it.product_id === p.id);
      if (existing >= 0) {
        const clone = cur.slice();
        clone[existing] = { ...clone[existing], quantity: clone[existing].quantity + 1 };
        return clone;
      }
      return [
        ...cur,
        { _key: `new_${Date.now()}_${p.id}`, product_id: p.id, quantity: 1, completed: false, notes: "" },
      ];
    });
    setProductSearch("");
  };
  const updateQty = (idx, qty) => {
    const q = Math.max(1, Number(qty) || 1);
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, quantity: q } : it)));
  };
  const updateNote = (idx, val) => {
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, notes: val } : it)));
  };
  const removeItem = (idx) => {
    setItems((cur) => cur.filter((_, i) => i !== idx));
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        title: title.trim(),
        type,
        from_location_id: fromLoc === NONE ? null : fromLoc,
        to_location_id: toLoc === NONE ? null : toLoc,
        notes: notes.trim() || null,
        items: items.map((it, idx) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          completed: !!it.completed,
          notes: it.notes || null,
          sort_order: idx,
        })),
      };
      if (isEdit) {
        await api.put(`/work-lists/${initial.id}`, {
          title: payload.title,
          type: payload.type,
          status,
          from_location_id: payload.from_location_id,
          to_location_id: payload.to_location_id,
          notes: payload.notes,
        });
        await api.put(`/work-lists/${initial.id}/items`, { items: payload.items });
      } else {
        await api.post("/work-lists", payload);
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? "Arbeitsliste aktualisiert" : "Arbeitsliste angelegt");
      onSaved();
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="wl-editor"
        className="bg-zinc-950 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-wide">
            {isEdit ? "Arbeitsliste bearbeiten" : "Neue Arbeitsliste"}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Arbeitslisten sind rein organisatorisch — kein Bestand wird angepasst.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">Titel</Label>
            <Input
              data-testid="wl-editor-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Nachschub Warenlager Nord — KW 12"
              className="bg-zinc-900 border-zinc-800 rounded-sm mt-1"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Typ</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="wl-editor-type" className="bg-zinc-900 border-zinc-800 rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  {TYPE_ORDER.map((t) => (
                    <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isEdit && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="wl-editor-status" className="bg-zinc-900 border-zinc-800 rounded-sm mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="offen">Offen</SelectItem>
                    <SelectItem value="erledigt">Erledigt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                Von (optional)
              </Label>
              <Select value={fromLoc} onValueChange={setFromLoc}>
                <SelectTrigger data-testid="wl-editor-from" className="bg-zinc-900 border-zinc-800 rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value={NONE}>— nicht gesetzt —</SelectItem>
                  {(locations || []).map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                Nach (optional)
              </Label>
              <Select value={toLoc} onValueChange={setToLoc}>
                <SelectTrigger data-testid="wl-editor-to" className="bg-zinc-900 border-zinc-800 rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value={NONE}>— nicht gesetzt —</SelectItem>
                  {(locations || []).map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Bemerkung
            </Label>
            <Textarea
              data-testid="wl-editor-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="bg-zinc-900 border-zinc-800 rounded-sm mt-1"
              placeholder="Optional — z. B. Ansprechperson, Deadline, Sonderhinweise."
            />
          </div>

          {/* Produkt-Suche + Add */}
          <div className="border-t border-zinc-800 pt-4">
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Positionen
            </Label>
            <div className="relative mt-1">
              <Input
                data-testid="wl-editor-search"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Produkt suchen (Name, EAN, SKU, Marke)…"
                className="bg-zinc-900 border-zinc-800 rounded-sm pl-9"
              />
              <Search size={14} className="absolute left-3 top-3 text-zinc-500" />
            </div>
            {productSearch.trim() && (
              <div
                data-testid="wl-editor-search-results"
                className="border border-zinc-800 bg-zinc-900 rounded-sm mt-1 max-h-56 overflow-y-auto divide-y divide-zinc-800"
              >
                {filteredProducts.length === 0 && (
                  <div className="text-xs text-zinc-500 p-3">Keine Treffer.</div>
                )}
                {filteredProducts.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`wl-editor-add-${p.id}`}
                    onClick={() => addItem(p)}
                    className="w-full text-left px-3 py-2 hover:bg-zinc-800/70 text-sm flex items-center justify-between gap-2"
                  >
                    <span className="truncate">
                      <span className="text-zinc-100">{p.name}</span>
                      {p.ean && <span className="ml-2 text-zinc-500 font-mono text-xs">{p.ean}</span>}
                    </span>
                    <Plus size={14} className="text-cyan-400 shrink-0" />
                  </button>
                ))}
              </div>
            )}

            <div className="mt-3 space-y-2">
              {items.length === 0 && (
                <div className="text-xs text-zinc-600 italic">
                  Keine Positionen. Suche oben ein Produkt und füge es hinzu.
                </div>
              )}
              {items.map((it, idx) => {
                const p = productMap[it.product_id];
                return (
                  <div
                    key={it._key}
                    data-testid={`wl-editor-item-${idx}`}
                    className="flex items-center gap-2 border border-zinc-800 bg-zinc-900 rounded-sm px-2 py-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-zinc-100 truncate">
                        {p?.name || it.product_id}
                      </div>
                      {p?.ean && <div className="text-xs text-zinc-500 font-mono">{p.ean}</div>}
                    </div>
                    <Input
                      data-testid={`wl-editor-qty-${idx}`}
                      type="number" min={1}
                      value={it.quantity}
                      onChange={(e) => updateQty(idx, e.target.value)}
                      className="w-20 h-9 bg-zinc-950 border-zinc-800 rounded-sm text-right font-mono"
                    />
                    <Input
                      data-testid={`wl-editor-note-${idx}`}
                      value={it.notes || ""}
                      onChange={(e) => updateNote(idx, e.target.value)}
                      placeholder="Bemerkung"
                      className="hidden sm:block flex-1 h-9 bg-zinc-950 border-zinc-800 rounded-sm text-xs"
                    />
                    <Button
                      data-testid={`wl-editor-remove-${idx}`}
                      variant="ghost" size="icon"
                      onClick={() => removeItem(idx)}
                      className="h-9 w-9 text-rose-400 hover:bg-rose-950/60"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            className="border-zinc-700 rounded-sm"
            onClick={() => onOpenChange(false)}
            data-testid="wl-editor-cancel"
          >
            Abbrechen
          </Button>
          <Button
            data-testid="wl-editor-save"
            disabled={!title.trim() || saveMut.isPending}
            onClick={() => saveMut.mutate()}
            className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 uppercase tracking-widest font-bold rounded-sm"
          >
            <Check size={16} className="mr-2" />
            {isEdit ? "Speichern" : "Anlegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
